import clsx from 'clsx'
import { rem } from 'polished'
import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import type { FileError } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import styled from 'styled-components'

import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from './constants'

import LineCloudUploadIcon from 'LuxKit/Icons/line/LineCloudUploadIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'

const DropzoneContainer = styled.div`
  cursor: pointer;
  border: 1px dashed ${props => props.theme.palette.neutral.default.five};
  background: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(16)} ${rem(20)};

  &.isDragging {
    background-color: ${props => props.theme.palette.neutral.default.five};
  }
`

const Underline = styled.span`
  text-decoration-line: underline;
`

interface Props {
  onSelectFile: (file: File) => void
  onRejectFile: (error: FileError) => void
  disabled?: boolean
}

function DocumentDropzone({ onSelectFile, onRejectFile, disabled }: Props) {
  const intl = useIntl()
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]

        if (/[%?#]/.test(file.name)) {
          onRejectFile({
            message: 'File name contains invalid characters (% ? #)',
            code: 'invalid-file-name',
          })
        } else {
          onSelectFile(file)
        }
      }
    },
    onDropRejected: (rejections) => {
      console.error('Files rejected:', rejections)
      if (rejections[0]) {
        onRejectFile(rejections[0].errors[0])
      }
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
      'image/heic': ['.heic'],
    },
    disabled,
  })

  return (
    <DropzoneContainer
      {...getRootProps()}
      className={clsx({ isDragging: isDragActive })}
    >
      <input {...getInputProps()} />
      <Group direction="vertical" gap={4} fullWidth horizontalAlign="center">
        <CSSBreakpoint max="tablet">
          <HeadingTextBlock variant="heading4" startIcon={<LineCloudUploadIcon />}>
            {intl.formatMessage({
              id: 'bzVAy37zZ0',
              defaultMessage: 'Browse files',
              description: 'Button label to browse and upload files in the document dropzone on mobile',
            })}
          </HeadingTextBlock>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <LineCloudUploadIcon size="L" />
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <HeadingTextBlock variant="heading4" align="center">
            <FormattedMessage
              id="Po+rU/M+d7"
              defaultMessage="Drag and drop or <underline>browse</underline>"
              description="Instruction text for the document dropzone on desktop"
              values={{
                underline: (chunks: React.ReactNode) => <Underline>{chunks}</Underline>,
              }}
            />
          </HeadingTextBlock>
        </CSSBreakpoint>
        <CaptionTextBlock
          variant="medium"
          colour="neutral-three"
          align="center"
        >
          {intl.formatMessage(
            {
              id: 'atxloHBOe4',
              defaultMessage: 'Accepted formats: .PDF, .JPEG, .PNG and .HEIC (max {maxFileSize}MB)',
              description: 'Accepted file formats and size limit for document upload dropzone',
            },
            { maxFileSize: MAX_FILE_SIZE_MB },
          )}
        </CaptionTextBlock>
      </Group>
    </DropzoneContainer>
  )
}

export default DocumentDropzone
